import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Loader2,
  Lock,
  Mail,
  ArrowLeft,
  Eye,
  EyeOff,
  User,
  Phone,
  Building2,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { LOGO_URL } from "../lib/constants";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    phone: "",
    company: "",
    marketing_opt_in: true,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) {
      toast.error("La contraseña debe tener al menos 6 caracteres.");
      return;
    }
    setLoading(true);
    const res = await register(form);
    setLoading(false);
    if (res.ok) {
      toast.success(`¡Bienvenido, ${res.user.name}!`, {
        description: "Su cuenta fue creada correctamente.",
      });
      nav("/perfil", { replace: true });
    } else {
      toast.error("No se pudo crear la cuenta", { description: res.error });
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-5 py-10 relative">
      <div className="absolute inset-0 bg-blueprint opacity-50 pointer-events-none" />
      <div className="relative w-full max-w-lg">
        <Link
          to="/"
          data-testid="back-to-site"
          className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-pv-green mb-6 transition-colors"
        >
          <ArrowLeft size={16} /> Volver al sitio
        </Link>
        <div className="bg-white border border-slate-200 rounded-sm p-8 sm:p-10">
          <div className="flex items-center gap-3 mb-6">
            <img src={LOGO_URL} alt="Pino Verde" className="h-10 w-10" />
            <div>
              <div className="font-display font-extrabold text-pv-green tracking-tight">
                PINO VERDE
              </div>
              <div className="overline text-pv-orange -mt-0.5">Crear cuenta</div>
            </div>
          </div>

          <h1 className="font-display font-bold text-pv-green text-2xl mb-1">
            Únase como cliente
          </h1>
          <p className="text-sm text-slate-500 mb-6">
            Reciba ofertas exclusivas y guarde sus cotizaciones.
          </p>

          <form onSubmit={onSubmit} className="space-y-4" data-testid="register-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="r-name" className="text-slate-700">
                  Nombre completo *
                </Label>
                <div className="relative mt-1.5">
                  <User
                    size={16}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                  />
                  <Input
                    id="r-name"
                    required
                    data-testid="register-name"
                    value={form.name}
                    onChange={(e) => set("name", e.target.value)}
                    placeholder="Juan Pérez"
                    className="pl-9 h-11 rounded-sm border-slate-300 focus-visible:ring-pv-green"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="r-phone" className="text-slate-700">
                  Teléfono
                </Label>
                <div className="relative mt-1.5">
                  <Phone
                    size={16}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                  />
                  <Input
                    id="r-phone"
                    data-testid="register-phone"
                    value={form.phone}
                    onChange={(e) => set("phone", e.target.value)}
                    placeholder="8888-8888"
                    className="pl-9 h-11 rounded-sm border-slate-300 focus-visible:ring-pv-green"
                  />
                </div>
              </div>
            </div>
            <div>
              <Label htmlFor="r-company" className="text-slate-700">
                Empresa
              </Label>
              <div className="relative mt-1.5">
                <Building2
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                />
                <Input
                  id="r-company"
                  data-testid="register-company"
                  value={form.company}
                  onChange={(e) => set("company", e.target.value)}
                  placeholder="Nombre de su empresa"
                  className="pl-9 h-11 rounded-sm border-slate-300 focus-visible:ring-pv-green"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="r-email" className="text-slate-700">
                Correo *
              </Label>
              <div className="relative mt-1.5">
                <Mail
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                />
                <Input
                  id="r-email"
                  type="email"
                  required
                  data-testid="register-email"
                  value={form.email}
                  onChange={(e) => set("email", e.target.value)}
                  placeholder="su@correo.com"
                  className="pl-9 h-11 rounded-sm border-slate-300 focus-visible:ring-pv-green"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="r-password" className="text-slate-700">
                Contraseña * <span className="text-xs text-slate-400">(mín. 6 caracteres)</span>
              </Label>
              <div className="relative mt-1.5">
                <Lock
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                />
                <Input
                  id="r-password"
                  type={showPassword ? "text" : "password"}
                  required
                  minLength={6}
                  data-testid="register-password"
                  value={form.password}
                  onChange={(e) => set("password", e.target.value)}
                  placeholder="••••••••"
                  className="pl-9 pr-10 h-11 rounded-sm border-slate-300 focus-visible:ring-pv-green"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-pv-green transition-colors"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <label className="flex items-start gap-3 pt-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.marketing_opt_in}
                onChange={(e) => set("marketing_opt_in", e.target.checked)}
                data-testid="register-optin"
                className="mt-0.5 h-4 w-4 accent-pv-green"
              />
              <span className="text-sm text-slate-600 leading-snug">
                Quiero recibir ofertas, novedades y promociones de Pino Verde
                por correo electrónico. (Puede darse de baja en cualquier
                momento desde su perfil.)
              </span>
            </label>
            <Button
              type="submit"
              disabled={loading}
              data-testid="register-submit"
              className="w-full h-11 rounded-sm bg-pv-orange hover:bg-pv-orange/90 text-white font-semibold"
            >
              {loading ? (
                <>
                  <Loader2 size={16} className="mr-2 animate-spin" />
                  Creando cuenta...
                </>
              ) : (
                "Crear cuenta"
              )}
            </Button>
          </form>

          <div className="mt-6 pt-6 border-t border-slate-100 text-center text-sm text-slate-600">
            ¿Ya tenés cuenta?{" "}
            <Link
              to="/login"
              data-testid="go-to-login"
              className="text-pv-orange font-semibold hover:underline"
            >
              Iniciar sesión
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
